import { create } from 'zustand';
import type { AppManifest, AppMeta, Asset } from '@scio3/shared-types';

export interface SmallAppEditorOptions {
  currentPath?: string | null;
  currentGroup?: { _id: string; teamId?: string; currentPath?: string } | null;
  onAssetSaved?: (asset: Partial<Asset>) => void;
  isCloseModalAfterSave?: boolean;
  /** Transient clone ID — when set, the iframe treats this as a clone operation */
  cloneId?: string | null;
  /** Engage wizard mode — sets engage=true and groupId=null before save mutation */
  isEngage?: boolean;
  /** Called with raw iframe form data during save — lets wizard capture fields not in the Asset record */
  onFormDataCapture?: (formData: Record<string, unknown>) => void;
}

/** One entry in the editor stack — a single open editor dialog instance. */
export interface EditorEntry {
  /** Stable id used to key React children + scope the iframe element id. */
  id: string;
  appMeta: AppMeta;
  /** null = add mode, existing asset = edit mode */
  asset: Partial<Asset> | null;
  options: SmallAppEditorOptions;
  /** Inline manifest — used by ManifestPlayground to pass manifest directly (bypasses API fetch) */
  inlineManifest: AppManifest | null;
}

interface SmallAppEditorState {
  /** Editor stack — `openEditor` pushes; `closeEditor` pops the top.
   *  Multiple entries support nested "Add new app" flows where a picker
   *  inside the parent editor opens another editor on top (Scrolling Strip
   *  → asset picker → Apps → pick News). Each entry renders its own
   *  dialog so nothing is lost when a child is opened on top. */
  stack: EditorEntry[];

  // Derived top-of-stack fields kept in state for back-compat with subscribers
  // that read these directly (selectors stay reactive without code changes).
  isOpen: boolean;
  appMeta: AppMeta | null;
  asset: Partial<Asset> | null;
  options: SmallAppEditorOptions;
  inlineManifest: AppManifest | null;

  openEditor: (params: {
    appMeta: AppMeta;
    asset?: Partial<Asset> | null;
    options?: SmallAppEditorOptions;
    /** Pass manifest inline to bypass API fetch (used by ManifestPlayground) */
    inlineManifest?: AppManifest | null;
  }) => void;
  /** Close the top entry. If the stack still has entries, the previous editor
   *  resurfaces with all its React state intact (it stayed mounted). */
  closeEditor: () => void;
  /** Force-close every editor in the stack — for security/access-revoked paths
   *  where we must tear down the entire chain regardless of nesting. */
  closeAllEditors: () => void;
  /** Shallow-merge a caller-constructed patch onto the top entry's asset.
   *  Used by Moderation dialog hooks (sort change, refresh) so the next reopen
   *  reads fresh values without a GET_ASSET refetch. Accepts arbitrary keys
   *  (e.g. `advancedOptions`) — `Asset` shared-type doesn't declare every
   *  smallapp/manifest field, so callers stay flexible. */
  updateAsset: (partial: Partial<Asset> & Record<string, unknown>) => void;
  /** Merge fresh server data into the in-memory asset (of the top entry) after
   *  a save round-trip so reopening or staying open (`keepOpenOnSave`) reflects
   *  the latest values without an extra GraphQL fetch. Preserves `meta` if the
   *  response omits it. */
  updateAssetFromSave: (savedAsset: Partial<Asset>) => void;
}

let entryIdCounter = 0;
const nextEntryId = () => `editor-${Date.now()}-${++entryIdCounter}`;

const deriveTop = (stack: EditorEntry[]) => {
  const top = stack[stack.length - 1];
  return {
    isOpen: !!top,
    appMeta: top?.appMeta ?? null,
    asset: top?.asset ?? null,
    options: top?.options ?? {},
    inlineManifest: top?.inlineManifest ?? null,
  };
};

export const useSmallAppEditorStore = create<SmallAppEditorState>((set) => ({
  stack: [],
  isOpen: false,
  appMeta: null,
  asset: null,
  options: {},
  inlineManifest: null,

  openEditor: ({ appMeta, asset = null, options = {}, inlineManifest = null }) =>
    set((state) => {
      const entry: EditorEntry = {
        id: nextEntryId(),
        appMeta,
        asset,
        options,
        inlineManifest,
      };
      const stack = [...state.stack, entry];
      return { stack, ...deriveTop(stack) };
    }),

  closeEditor: () =>
    set((state) => {
      if (state.stack.length === 0) return state;
      const stack = state.stack.slice(0, -1);
      return { stack, ...deriveTop(stack) };
    }),

  closeAllEditors: () =>
    set(() => ({
      stack: [],
      isOpen: false,
      appMeta: null,
      asset: null,
      options: {},
      inlineManifest: null,
    })),

  updateAsset: (partial) =>
    set((state) => {
      if (state.stack.length === 0) return state;
      const stack = state.stack.slice();
      const top = stack[stack.length - 1];
      if (!top.asset) return state;
      stack[stack.length - 1] = { ...top, asset: { ...top.asset, ...partial } };
      return { stack, ...deriveTop(stack) };
    }),

  updateAssetFromSave: (savedAsset) =>
    set((state) => {
      if (state.stack.length === 0) return state;
      const stack = state.stack.slice();
      const top = stack[stack.length - 1];
      // Flat-merge top-level fields, but preserve `meta` when the response omits it
      // (Apollo only writes what's in the SAVE_ASSET selection — apps whose custom
      // fields live in `meta.*` would otherwise lose sub-keys not echoed back).
      const merged: Partial<Asset> = top.asset
        ? { ...top.asset, ...savedAsset, meta: savedAsset.meta ?? top.asset.meta }
        : savedAsset;
      stack[stack.length - 1] = { ...top, asset: merged };
      return { stack, ...deriveTop(stack) };
    }),
}));
